/**
 * How many solutions does a grid of clues have? The build refuses to ship anything
 * that does not answer "exactly one", so this file decides what reaches the site.
 *
 * It works one line at a time. For a line of `n` squares and clues `c`, two dynamic
 * programmes run over it: `pre[i][j]` asks whether the first `j` runs fit inside the
 * first `i` squares, and `suf[i][j]` whether runs `j` onwards fit from square `i` to
 * the end. Together they decide, for every square, whether any legal arrangement of
 * that line fills it and whether any leaves it blank. A square only one of those is
 * true for is settled — that is the fact the line gives up — and a square neither is
 * true for means the line contradicts what is already on the board.
 *
 * Deciding it this way rather than by listing the line's arrangements is what makes
 * the answer trustworthy on a large grid. Listing them is exponential: a 25-square
 * line with clues `1 1 1 1` has 7315 arrangements, and a solver that keeps the first
 * few thousand and drops the rest will report a grid as unsolvable when the drawing
 * in front of you plainly solves it. The programmes above are O(n·c) whatever the
 * grid's size, so nothing is dropped and no size needs its own code path.
 *
 * Line reasoning alone does not finish every grid. What is left over is guessed at:
 * pick the most constrained undecided square, try it filled, then try it blank. That
 * is `analyzeSolutions`, and it is also why it reports whether it actually finished —
 * see the `complete` flag.
 */

/**
 * Every legal arrangement of one line's runs, as bitmasks.
 *
 * This is the teaching pages' enumerator, not the solver's: lineLogic.ts uses it to
 * show a reader the arrangements a line really has. It is capped, so it must not be
 * used to decide solution counts — `analyzeSolutions` reasons about the same
 * arrangements without listing them, and stays exact when this would truncate.
 */
export function generateLineMasks(length: number, clues: readonly number[]): number[] {
  if (clues.length === 0) return [0];
  const masks: number[] = [];

  function place(runIndex: number, minimumStart: number, mask: number): void {
    const runLength = clues[runIndex];
    if (runLength === undefined) {
      masks.push(mask);
      return;
    }

    const remainingRuns = clues.slice(runIndex + 1);
    const reserved = remainingRuns.reduce((sum, run) => sum + run, 0) + remainingRuns.length;
    const maximumStart = length - runLength - reserved;

    for (let start = minimumStart; start <= maximumStart; start += 1) {
      if (masks.length >= 2000) return;
      let nextMask = mask;
      for (let offset = 0; offset < runLength; offset += 1) {
        nextMask |= 1 << (start + offset);
      }
      place(runIndex + 1, start + runLength + 1, nextMask);
    }
  }

  place(0, 0, 0);
  return masks;
}

/** Reusable buffers, sized once per solve so the line pass allocates nothing. */
type LineScratch = {
  size: number;
  clueStride: number;
  line: Int8Array;
  /** `fillable[i]` counts squares below `i` that are not already crossed out. */
  fillable: Int32Array;
  pre: Uint8Array;
  suf: Uint8Array;
  /** Difference array: a run's span is marked at both ends and summed once. */
  spans: Int32Array;
  canBeFilled: Uint8Array;
  canBeEmpty: Uint8Array;
  /** Which lines still have something to say. A guess only disturbs two of them. */
  rowDirty: Uint8Array;
  columnDirty: Uint8Array;
  /** Offsets `settleLine` just settled, so the crossing lines can be requeued. */
  settledOffsets: Int32Array;
  settledCount: number;
};

function createScratch(size: number): LineScratch {
  // A line of n squares holds at most ceil(n / 2) runs, each needing a gap after it.
  const clueStride = Math.ceil(size / 2) + 2;
  return {
    size,
    clueStride,
    line: new Int8Array(size),
    fillable: new Int32Array(size + 1),
    pre: new Uint8Array((size + 1) * clueStride),
    suf: new Uint8Array((size + 1) * clueStride),
    spans: new Int32Array(size + 2),
    canBeFilled: new Uint8Array(size),
    canBeEmpty: new Uint8Array(size),
    rowDirty: new Uint8Array(size),
    columnDirty: new Uint8Array(size),
    settledOffsets: new Int32Array(size),
    settledCount: 0,
  };
}

type LineOutcome = { valid: boolean; changed: boolean };

const CONTRADICTION: LineOutcome = { valid: false, changed: false };

/**
 * Settles every square of one line that its clues leave no choice about, and reports
 * whether the line can be satisfied at all.
 *
 * Reads and writes the board directly: `-1` is undecided, `0` crossed out, `1` filled.
 */
function settleLine(
  cells: Int8Array,
  size: number,
  lineIndex: number,
  isRow: boolean,
  clues: readonly number[],
  scratch: LineScratch,
): LineOutcome {
  const { line, fillable, pre, suf, spans, canBeFilled, canBeEmpty, settledOffsets, clueStride } = scratch;
  const runCount = clues.length;
  scratch.settledCount = 0;
  if (runCount + 1 > clueStride) return CONTRADICTION;

  const indexOf = (offset: number): number => (isRow ? lineIndex * size + offset : offset * size + lineIndex);

  for (let offset = 0; offset < size; offset += 1) {
    line[offset] = cells[indexOf(offset)] ?? -1;
  }

  fillable[0] = 0;
  for (let offset = 0; offset < size; offset += 1) {
    fillable[offset + 1] = (fillable[offset] ?? 0) + (line[offset] === 0 ? 0 : 1);
  }
  /** Can squares `from`..`to` all be filled — is none of them already crossed out? */
  const spanFillable = (from: number, to: number): boolean =>
    (fillable[to + 1] ?? 0) - (fillable[from] ?? 0) === to - from + 1;

  // pre[i][j]: runs 0..j-1 fit inside squares 0..i-1, with every other square blank.
  pre[0] = 1;
  for (let j = 1; j <= runCount; j += 1) pre[j] = 0;
  for (let i = 1; i <= size; i += 1) {
    const row = i * clueStride;
    for (let j = 0; j <= runCount; j += 1) {
      let reachable = 0;
      // Square i-1 left blank.
      if (line[i - 1] !== 1 && pre[(i - 1) * clueStride + j] === 1) reachable = 1;
      // Square i-1 is the last square of run j-1.
      if (reachable === 0 && j > 0) {
        const runLength = clues[j - 1] ?? 0;
        const start = i - runLength;
        if (start >= 0 && spanFillable(start, i - 1)) {
          if (start === 0) {
            if (pre[j - 1] === 1) reachable = 1;
          } else if (line[start - 1] !== 1 && pre[(start - 1) * clueStride + (j - 1)] === 1) {
            reachable = 1;
          }
        }
      }
      pre[row + j] = reachable;
    }
  }

  if (pre[size * clueStride + runCount] !== 1) return CONTRADICTION;

  // suf[i][j]: runs j..end fit inside squares i..size-1.
  const lastRow = size * clueStride;
  for (let j = 0; j <= runCount; j += 1) suf[lastRow + j] = j === runCount ? 1 : 0;
  for (let i = size - 1; i >= 0; i -= 1) {
    const row = i * clueStride;
    for (let j = 0; j <= runCount; j += 1) {
      let reachable = 0;
      if (line[i] !== 1 && suf[(i + 1) * clueStride + j] === 1) reachable = 1;
      if (reachable === 0 && j < runCount) {
        const runLength = clues[j] ?? 0;
        const end = i + runLength - 1;
        if (end <= size - 1 && spanFillable(i, end)) {
          if (end === size - 1) {
            if (suf[lastRow + j + 1] === 1) reachable = 1;
          } else if (line[end + 1] !== 1 && suf[(end + 2) * clueStride + (j + 1)] === 1) {
            reachable = 1;
          }
        }
      }
      suf[row + j] = reachable;
    }
  }

  // Which squares some legal arrangement leaves blank: the runs split either side of it.
  for (let offset = 0; offset < size; offset += 1) {
    let possible = 0;
    if (line[offset] !== 1) {
      for (let j = 0; j <= runCount; j += 1) {
        if (pre[offset * clueStride + j] === 1 && suf[(offset + 1) * clueStride + j] === 1) {
          possible = 1;
          break;
        }
      }
    }
    canBeEmpty[offset] = possible;
  }

  // Which squares some legal arrangement fills: walk every placement of every run and
  // mark the span it covers. The difference array keeps that linear in the line length.
  spans.fill(0);
  for (let j = 0; j < runCount; j += 1) {
    const runLength = clues[j] ?? 0;
    for (let start = 0; start + runLength - 1 < size; start += 1) {
      const end = start + runLength - 1;
      if (!spanFillable(start, end)) continue;
      const startFits = start === 0
        ? pre[j] === 1
        : line[start - 1] !== 1 && pre[(start - 1) * clueStride + j] === 1;
      if (!startFits) continue;
      const endFits = end === size - 1
        ? suf[lastRow + j + 1] === 1
        : line[end + 1] !== 1 && suf[(end + 2) * clueStride + (j + 1)] === 1;
      if (!endFits) continue;
      spans[start] = (spans[start] ?? 0) + 1;
      spans[end + 1] = (spans[end + 1] ?? 0) - 1;
    }
  }
  let covering = 0;
  for (let offset = 0; offset < size; offset += 1) {
    covering += spans[offset] ?? 0;
    canBeFilled[offset] = covering > 0 ? 1 : 0;
  }

  let changed = false;
  for (let offset = 0; offset < size; offset += 1) {
    const filled = canBeFilled[offset] === 1;
    const empty = canBeEmpty[offset] === 1;
    if (filled && empty) continue;
    if (!filled && !empty) return { valid: false, changed };

    const index = indexOf(offset);
    const settled = filled ? 1 : 0;
    if (cells[index] === -1) {
      cells[index] = settled;
      changed = true;
      settledOffsets[scratch.settledCount] = offset;
      scratch.settledCount += 1;
    } else if (cells[index] !== settled) {
      return { valid: false, changed };
    }
  }

  return { valid: true, changed };
}

/**
 * Runs line reasoning over rows and columns until nothing more is forced.
 *
 * Only lines with something new to look at are examined. A guess disturbs one row
 * and one column, and settling a square in a row gives only that square's column
 * anything to reconsider — so re-examining all of both after every guess is work
 * thrown away, and on a large grid that waste is the whole running time.
 */
function propagate(
  cells: Int8Array,
  size: number,
  rowClues: readonly (readonly number[])[],
  columnClues: readonly (readonly number[])[],
  scratch: LineScratch,
  seedIndex?: number,
): boolean {
  const { rowDirty, columnDirty, settledOffsets } = scratch;

  if (seedIndex === undefined) {
    rowDirty.fill(1);
    columnDirty.fill(1);
  } else {
    rowDirty.fill(0);
    columnDirty.fill(0);
    rowDirty[Math.floor(seedIndex / size)] = 1;
    columnDirty[seedIndex % size] = 1;
  }

  let progressed = true;
  while (progressed) {
    progressed = false;

    for (let row = 0; row < size; row += 1) {
      if (rowDirty[row] !== 1) continue;
      rowDirty[row] = 0;
      progressed = true;
      const outcome = settleLine(cells, size, row, true, rowClues[row] ?? [], scratch);
      if (!outcome.valid) return false;
      for (let at = 0; at < scratch.settledCount; at += 1) {
        columnDirty[settledOffsets[at] ?? 0] = 1;
      }
    }

    for (let column = 0; column < size; column += 1) {
      if (columnDirty[column] !== 1) continue;
      columnDirty[column] = 0;
      progressed = true;
      const outcome = settleLine(cells, size, column, false, columnClues[column] ?? [], scratch);
      if (!outcome.valid) return false;
      for (let at = 0; at < scratch.settledCount; at += 1) {
        rowDirty[settledOffsets[at] ?? 0] = 1;
      }
    }
  }
  return true;
}

/**
 * The undecided square to guess at: the one in the line with the fewest unknowns
 * left, because that line is closest to forcing the rest of itself.
 */
function chooseBranchCell(cells: Int8Array, size: number): number {
  let best = -1;
  let bestUnknowns = Number.POSITIVE_INFINITY;

  for (let line = 0; line < size; line += 1) {
    let rowUnknowns = 0;
    let rowFirst = -1;
    let columnUnknowns = 0;
    let columnFirst = -1;
    for (let offset = 0; offset < size; offset += 1) {
      if (cells[line * size + offset] === -1) {
        rowUnknowns += 1;
        if (rowFirst === -1) rowFirst = line * size + offset;
      }
      if (cells[offset * size + line] === -1) {
        columnUnknowns += 1;
        if (columnFirst === -1) columnFirst = offset * size + line;
      }
    }
    if (rowFirst !== -1 && rowUnknowns < bestUnknowns) {
      bestUnknowns = rowUnknowns;
      best = rowFirst;
    }
    if (columnFirst !== -1 && columnUnknowns < bestUnknowns) {
      bestUnknowns = columnUnknowns;
      best = columnFirst;
    }
  }

  return best;
}

export type SolutionReport = {
  /** Solutions found, and never more than the `maximum` asked for. */
  solutions: number;
  /**
   * False when the search ran out of budget before it had looked everywhere. The
   * count is then only a lower bound: it says nothing about uniqueness, and a caller
   * that reports "ambiguous" on the strength of it is reporting its own timeout.
   */
  complete: boolean;
};

export type AnalyzeOptions = {
  /** Stop once this many solutions are found. */
  maximum?: number;
  /** How many guesses the search may make before giving up. */
  nodeBudget?: number;
  /** Wall-clock ceiling. The real guard: a node's cost grows with the grid. */
  deadlineMs?: number;
};

/** A drawing that reads as a picture is settled by line reasoning in a handful of
    guesses. These are here to bound a grid of noise, not to bound real work. */
const DEFAULT_NODE_BUDGET = 200000;
const DEFAULT_DEADLINE_MS = 5000;
/** Reading the clock has a cost of its own, and a node is never long enough to
    overshoot a deadline by much. */
const NODES_PER_CLOCK_CHECK = 64;

/**
 * Counts a grid's solutions, and says whether it finished counting.
 *
 * Prefer this over `countSolutions` anywhere the difference between "this grid is
 * ambiguous" and "we stopped looking" is shown to someone.
 */
export function analyzeSolutions(
  rowClues: readonly (readonly number[])[],
  columnClues: readonly (readonly number[])[],
  options: AnalyzeOptions = {},
): SolutionReport {
  const maximum = options.maximum ?? 2;
  const nodeBudget = options.nodeBudget ?? DEFAULT_NODE_BUDGET;
  const deadline = Date.now() + (options.deadlineMs ?? DEFAULT_DEADLINE_MS);
  const size = rowClues.length;
  if (size === 0 || columnClues.length !== size) return { solutions: 0, complete: true };

  // A clue of zero or less is not a run, and the line programmes assume it is one.
  for (const clues of [...rowClues, ...columnClues]) {
    for (const clue of clues) {
      if (!Number.isInteger(clue) || clue <= 0) return { solutions: 0, complete: true };
    }
  }

  const scratch = createScratch(size);
  let solutions = 0;
  let nodes = 0;
  let exhausted = false;

  function search(cells: Int8Array, seedIndex?: number): void {
    if (exhausted || solutions >= maximum) return;
    nodes += 1;
    if (nodes > nodeBudget || (nodes % NODES_PER_CLOCK_CHECK === 0 && Date.now() > deadline)) {
      exhausted = true;
      return;
    }

    if (!propagate(cells, size, rowClues, columnClues, scratch, seedIndex)) return;

    const branchIndex = chooseBranchCell(cells, size);
    if (branchIndex === -1) {
      solutions += 1;
      return;
    }

    for (const guess of [1, 0] as const) {
      const next = new Int8Array(cells);
      next[branchIndex] = guess;
      search(next, branchIndex);
      if (exhausted || solutions >= maximum) return;
    }
  }

  const cells = new Int8Array(size * size);
  cells.fill(-1);
  search(cells);

  return { solutions, complete: !exhausted };
}

/**
 * How many solutions, capped at `maximum`.
 *
 * A search that gives up reports `maximum`, so a caller checking `=== 1` treats
 * "we could not finish" the same as "more than one" and fails closed. That is the
 * right default for the build; it is the wrong answer to show a person, which is
 * what `analyzeSolutions` is for.
 */
export function countSolutions(
  rowClues: readonly (readonly number[])[],
  columnClues: readonly (readonly number[])[],
  maximum = 2,
): number {
  const report = analyzeSolutions(rowClues, columnClues, { maximum });
  return report.complete ? report.solutions : Math.max(report.solutions, maximum);
}

/**
 * How deep a solver must reach to finish the grid, as the number of nested
 * assumptions it has to try:
 *
 *   1 — line by line. Propagation alone fills every square: each deduction comes
 *       from reading one row or column at a time.
 *   2 — one careful assumption at a time. Some squares only fall after guessing a
 *       square and watching the guess contradict — the corner-reading technique.
 *   3 — look-ahead. Progress needs assumptions inside assumptions before a square
 *       is certain.
 *   4 — deep chains. Anything the shallower passes could not finish.
 *
 * The branch point is the same `chooseBranchCell` the counter uses, so a tier
 * describes the grid the way the searcher experiences it. The caller must already
 * know the puzzle has exactly one solution — grading a puzzle with several would
 * return the tier of whichever branch the search met first, and a puzzle with none
 * would spin to the node cap.
 */
export type SolveTier = 1 | 2 | 3 | 4;

// A guard so one pathological grid cannot hold a build hostage. Unique-solution
// puzzles the shallow passes cannot finish are rare, and those are exactly the
// ones worth reporting as tier 4 rather than grading precisely.
const GRADING_NODE_CAP = 250_000;

export function gradeSolveTier(
  rowClues: readonly (readonly number[])[],
  columnClues: readonly (readonly number[])[],
): SolveTier {
  const size = rowClues.length;
  if (size === 0 || columnClues.length !== size) return 4;
  // The line programmes assume every clue is a run; anything else is not gradeable.
  for (const clues of [...rowClues, ...columnClues]) {
    for (const clue of clues) {
      if (!Number.isInteger(clue) || clue <= 0) return 4;
    }
  }

  const scratch = createScratch(size);
  let nodes = 0;

  // Returns true when the grid can be finished without guessing more than `budget`
  // squares deep. The guess is exactly a player's pencil mark: try this square
  // filled, and if the grid will not have it, try it blank.
  const searchWithin = (cells: Int8Array, budget: number, seedIndex?: number): boolean => {
    nodes += 1;
    if (nodes > GRADING_NODE_CAP || !propagate(cells, size, rowClues, columnClues, scratch, seedIndex)) return false;

    const branchIndex = chooseBranchCell(cells, size);
    if (branchIndex === -1) return true;
    if (budget === 0) return false;

    for (const guess of [1, 0] as const) {
      const next = new Int8Array(cells);
      next[branchIndex] = guess;
      if (searchWithin(next, budget - 1, branchIndex)) return true;
    }
    return false;
  };

  for (const tier of [1, 2, 3] as const) {
    nodes = 0;
    const cells = new Int8Array(size * size);
    cells.fill(-1);
    if (searchWithin(cells, tier - 1)) return tier;
  }
  return 4;
}

/**
 * Up to `maximum` full solutions, for the art desk: when a drawing admits two,
 * the difference between them is exactly the squares to nudge. Returns the
 * solutions found and whether the search finished, so a caller never mistakes
 * truncation for completeness.
 */
export function listSolutions(
  rowClues: readonly (readonly number[])[],
  columnClues: readonly (readonly number[])[],
  maximum = 4,
): { solutions: Int8Array[]; complete: boolean } {
  const nodeBudget = 200_000;
  const deadline = Date.now() + 5_000;
  const size = rowClues.length;
  if (size === 0 || columnClues.length !== size) return { solutions: [], complete: true };

  const scratch = createScratch(size);
  const found: Int8Array[] = [];
  let nodes = 0;
  let exhausted = false;

  function search(cells: Int8Array, seedIndex?: number): void {
    if (exhausted || found.length >= maximum) return;
    nodes += 1;
    if (nodes > nodeBudget || (nodes % NODES_PER_CLOCK_CHECK === 0 && Date.now() > deadline)) {
      exhausted = true;
      return;
    }

    if (!propagate(cells, size, rowClues, columnClues, scratch, seedIndex)) return;

    const branchIndex = chooseBranchCell(cells, size);
    if (branchIndex === -1) {
      found.push(new Int8Array(cells));
      return;
    }

    for (const guess of [1, 0] as const) {
      const next = new Int8Array(cells);
      next[branchIndex] = guess;
      search(next, branchIndex);
      if (exhausted || found.length >= maximum) return;
    }
  }

  const cells = new Int8Array(size * size);
  cells.fill(-1);
  search(cells);

  return { solutions: found, complete: !exhausted };
}
